import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import colors from "../assets/theme/colors";
import { useChildContext } from "../contexts/ChildContext";
import { useRole } from "../contexts/RoleContext";
import { KnowledgeItemsSection } from "../features/knowledgeItems";
import ChildShellBottomNav from "../components/Dashboard/shared/ChildShellBottomNav";
import { getChildAccentTheme } from "../components/Dashboard/shared/childAccentTheme";
import { ACTIVE_CHILD_STORAGE_KEY } from "../components/Dashboard/shared/DashboardViewContext";
import CareProfileBackButton from "../components/UI/CareProfileBackButton";

const getSessionChildId = () => {
  try {
    return window.sessionStorage.getItem("lifelog:last-care-knowledge-child-id") || "";
  } catch (error) {
    return "";
  }
};

const setSessionChildId = (childId = "") => {
  try {
    window.sessionStorage.setItem("lifelog:last-care-knowledge-child-id", childId);
  } catch (error) {
    // Route state and child context still carry the selected child.
  }
};

const CareKnowledgePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentChildId, setCurrentChildId } = useChildContext();
  const { childrenWithAccess = [] } = useRole();
  const searchParams = new URLSearchParams(location.search || "");
  const initialChildId = location.state?.childId
    || searchParams.get("childId")
    || currentChildId
    || getSessionChildId();
  const [selectedChildId, setSelectedChildId] = useState(initialChildId);

  const selectedChild = childrenWithAccess.find((child) => child.id === selectedChildId)
    || childrenWithAccess[0]
    || null;
  const resolvedChildId = selectedChild?.id || "";
  const childAccent = useMemo(
    () => getChildAccentTheme(selectedChild || {}, { children: childrenWithAccess }),
    [selectedChild, childrenWithAccess]
  );

  useEffect(() => {
    if (!selectedChildId && selectedChild?.id) {
      setSelectedChildId(selectedChild.id);
    }
  }, [selectedChild?.id, selectedChildId]);

  useEffect(() => {
    if (!resolvedChildId) return;
    setCurrentChildId?.(resolvedChildId);
    setSessionChildId(resolvedChildId);
  }, [resolvedChildId, setCurrentChildId]);

  useEffect(() => {
    const handleActiveChildChanged = (event) => {
      const nextChildId = event?.detail?.childId
        || window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY)
        || "";
      if (nextChildId) {
        setSelectedChildId(nextChildId);
        setCurrentChildId?.(nextChildId);
        setSessionChildId(nextChildId);
      }
    };

    window.addEventListener("captureez:active-child-changed", handleActiveChildChanged);
    return () => window.removeEventListener("captureez:active-child-changed", handleActiveChildChanged);
  }, [setCurrentChildId]);

  const navigateToDashboard = useCallback((target = "today") => {
    if (resolvedChildId) {
      setCurrentChildId?.(resolvedChildId);
      setSessionChildId(resolvedChildId);
    }

    navigate("/dashboard", {
      state: {
        selectedChildId: resolvedChildId || null,
        childShellTarget: target,
      },
    });
  }, [navigate, resolvedChildId, setCurrentChildId]);

  const handleChildShellNavSelect = useCallback((itemId) => {
    if (itemId === "home") {
      navigate("/home", { state: { selectedChildId: resolvedChildId || null } });
      return;
    }

    if (itemId === "profile") {
      navigate("/care-profile", {
        state: { childId: resolvedChildId || null },
      });
      return;
    }

    if (itemId === "today" || itemId === "tools") {
      navigateToDashboard(itemId);
      return;
    }

  }, [navigate, navigateToDashboard, resolvedChildId]);

  const handleBackToProfile = useCallback(() => {
    navigate("/care-profile", {
      state: { childId: resolvedChildId || null },
    });
  }, [navigate, resolvedChildId]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.semantic.sageMist,
        py: { xs: 1.5, md: 3 },
        pb: { xs: "calc(6rem + env(safe-area-inset-bottom, 0px))", md: 3 },
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={1.6}>
          <Stack
            component="header"
            spacing={1}
            sx={{
              pb: 1.25,
              borderBottom: `1px solid ${alpha(colors.semantic.ink, 0.12)}`,
            }}
          >
            <CareProfileBackButton
              onClick={handleBackToProfile}
              ariaLabel="Back to Profile"
            />
            <Box>
              <Typography component="h1" sx={{ fontSize: { xs: "1.32rem", md: "1.58rem" }, fontWeight: 500, color: colors.semantic.ink, lineHeight: 1.05 }}>
                Everything collected about {selectedChild?.name || "this person"}
              </Typography>
              <Typography sx={{ mt: 0.35, color: alpha(colors.semantic.ink, 0.68), fontSize: "0.82rem", lineHeight: 1.35 }}>
                Notes, documents, photos, voice recordings, emails, and other information collected over time.
              </Typography>
            </Box>
          </Stack>

          <KnowledgeItemsSection childId={resolvedChildId} child={selectedChild} />
        </Stack>
      </Container>
      <ChildShellBottomNav
        activeItem="profile"
        accent={childAccent}
        onSelect={handleChildShellNavSelect}
      />
    </Box>
  );
};

export default CareKnowledgePage;
